// Image decode helper — handles EXIF orientation + HEIC lazy-load.
// All inputs flow through here on the way into the renderer.

import { decodeHeic, isHeicFile } from "./heic.js";

export interface DecodedInput {
  bitmap: ImageBitmap;
  width: number;
  height: number;
  /** Notes returned to UI: bg-too-large warnings, HEIC decode notes, etc. */
  notes: string[];
}

const MAX_DIMENSION = 4096;

/**
 * Decode a File to an ImageBitmap with EXIF orientation applied.
 * If the input exceeds MAX_DIMENSION on either axis, downscales while
 * preserving aspect ratio.
 */
export async function decodeImage(file: File): Promise<DecodedInput> {
  const notes: string[] = [];

  let blob: Blob = file;
  if (isHeicFile(file)) {
    blob = await decodeHeic(file);
    notes.push(`HEIC decoded → PNG (${(blob.size / 1024).toFixed(0)} KB)`);
  }

  // createImageBitmap honours EXIF orientation when imageOrientation is set,
  // so iPhone photos arrive upright without manual rotation maths.
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob, {
      imageOrientation: "from-image",
    });
  } catch (err) {
    // Some browsers/format combos throw — retry without the hint.
    bitmap = await createImageBitmap(blob);
    notes.push(`decode fallback (no EXIF orientation): ${(err as Error).message}`);
  }

  if (bitmap.width > MAX_DIMENSION || bitmap.height > MAX_DIMENSION) {
    const scale = MAX_DIMENSION / Math.max(bitmap.width, bitmap.height);
    const newW = Math.round(bitmap.width * scale);
    const newH = Math.round(bitmap.height * scale);
    notes.push(
      `input ${bitmap.width}×${bitmap.height} 過大 → 縮到 ${newW}×${newH}(避免 Canvas limit)`,
    );
    const resized = await createImageBitmap(bitmap, 0, 0, bitmap.width, bitmap.height, {
      resizeWidth: newW,
      resizeHeight: newH,
      resizeQuality: "high",
    });
    bitmap.close();
    return { bitmap: resized, width: newW, height: newH, notes };
  }

  return { bitmap, width: bitmap.width, height: bitmap.height, notes };
}

export function stemOf(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i > 0 ? filename.slice(0, i) : filename;
}
